import {
    Card,
    CardContent,
    Chip,
    FormControl,
    FormControlLabel,
    FormLabel,
    Grid2 as Grid,
    Radio,
    RadioGroup,
    Stack,
    TextField,
} from "@mui/material"
import { useEffect, useState } from "react"
import { Label } from "../../components/Typography/Typography"
import { FlightlineEventReview, GeometryQCStatus, geometryQCStatusOptions } from "../../models/MissionReview"
import { ReviewType, canSplitReview } from "../../utils/MissionReviewUtils"
import { useMissionReviewStore } from "../../zstore/MissionReviewStore"
import { AddNewShapeSplitCard } from "./AddNewShapeSplitCard"
import { ReviewCardDistanceNumberFields } from "./DistanceSplit/ReviewCardDistanceNumberFields"
import { ReviewCardNodeNumberFields } from "./NodeSplit/ReviewCardNodeNumberFields"

const reviewCardMaxWidth = 480

interface ReviewCardProps {
    review: FlightlineEventReview
    index: number
    isLastCard: boolean
}

export const ReviewEventCard = ({ review, index, isLastCard }: ReviewCardProps) => {
    const selectedReviews = useMissionReviewStore((state) => state.currentPartialLines) ?? []
    const updatePartialLines = useMissionReviewStore((state) => state.updatePartialLines)
    const [qcComment, setQcComment] = useState("")

    useEffect(() => {
        // Adding a new card had a bug where it would use the review that used to have that index
        setQcComment(review.qc_comment ?? "")
    }, [review.qc_comment])

    const handleUpdateLine = <V,>(field: keyof FlightlineEventReview, value: V, index: number) => {
        const currentLine = selectedReviews[index]
        const updateLines = selectedReviews.slice()

        updateLines.splice(index, 1, { ...currentLine, [field]: value })
        updatePartialLines(updateLines)
    }

    return (
        <Stack>
            <AddNewCard index={index} review={review} />
            <Card sx={{ maxWidth: reviewCardMaxWidth }}>
                <CardContent>
                    <Grid size={{ md: 12 }}>
                        <Label>
                            Segment {review.segment_number ?? "1"}
                            {!review.id && <Chip label={"New"} color="info" size="small" sx={{ ml: 2, mt: -0.5 }} />}
                        </Label>
                    </Grid>
                    <Grid size={{ md: 12 }}>
                        <FormControl sx={{ pb: 2 }}>
                            <FormLabel>Review Status</FormLabel>
                            <RadioGroup
                                value={review.qc_status ?? GeometryQCStatus.ACCEPTED}
                                onChange={(_, value) => handleUpdateLine("qc_status", value, index)}
                                row
                            >
                                {geometryQCStatusOptions.map((status, idx) => (
                                    <FormControlLabel
                                        key={idx}
                                        value={status}
                                        control={<Radio size="small" />}
                                        label={status}
                                    />
                                ))}
                            </RadioGroup>
                        </FormControl>
                    </Grid>
                    {canSplitReview(review) && review.node_info && (
                        <ReviewCardNodeNumberFields
                            startNodeName={review.node_info.start_node_name}
                            stopNodeName={review.node_info.stop_node_name}
                            reviewCardIndex={index}
                        />
                    )}
                    {canSplitReview(review) && review.distance_info && (
                        <ReviewCardDistanceNumberFields
                            start_distance_nm={review.distance_info.start_distance_nm}
                            stop_distance_nm={review.distance_info.stop_distance_nm}
                            reviewCardIndex={index}
                        />
                    )}
                    <Grid size={{ md: 12 }}>
                        <TextField
                            fullWidth
                            value={qcComment}
                            label={"Review Comment"}
                            onChange={(event) => setQcComment(event.target.value)}
                            onBlur={() => {
                                const trimmedComment = qcComment.trim()
                                setQcComment(trimmedComment)
                                handleUpdateLine("qc_comment", trimmedComment, index)
                            }}
                        />
                    </Grid>
                </CardContent>
            </Card>
            {isLastCard && <AddNewCard index={index + 1} review={review} />}
        </Stack>
    )
}

const AddNewCard = ({ index, review }: { index: number; review: FlightlineEventReview }) => {
    if (!canSplitReview(review)) {
        return null
    }
    if (review.distance_info && review.node_info) {
        throw new Error("Review cannot have distance info and node info at the same time.")
    }
    return <AddNewShapeSplitCard index={index} reviewType={review.node_info ? ReviewType.NODE : ReviewType.DISTANCE} />
}
